import { PROJECT_TASK_TERMINAL_STATUSES } from '@/lib/constants';
import type { ProjectTask } from '@/types';
import { differenceInCalendarDays } from 'date-fns';

/**
 * Statuses that end a task — a finished task is never late.
 *
 * Seeded from the build-time config so it is never empty, then replaced with
 * the live set once the settings load. It is module state rather than a hook
 * because the Schedule *filter* lives in the table descriptor, which is a plain
 * object the client-table machinery calls without React context. Badge, filter
 * and sort all read this one value, which is the point: they have to agree on
 * what "overdue" means, and an administrator marking a custom status terminal
 * has to move all three at once.
 */
let terminalStatuses: readonly string[] = PROJECT_TASK_TERMINAL_STATUSES;

/** Called by SettingsProvider once the DB-configured statuses arrive. */
export function setTaskTerminalStatuses(statuses: Array<{ value: string; isTerminal?: boolean }>): void {
    const configured = statuses.filter((status) => status.isTerminal).map((status) => status.value);

    // An empty list means the settings have not loaded yet, not that nothing is
    // terminal — keep the build-time set rather than calling every done task late.
    if (configured.length > 0) {
        terminalStatuses = configured;
    }
}

export function isTaskTerminalStatus(status: unknown): boolean {
    return terminalStatuses.includes(String(status ?? ''));
}

/**
 * How a task stands against its end date.
 *
 * Shared rather than computed in the cell: the Schedule column, its filter and
 * its sort all have to agree on what "overdue" means, and they did not while
 * each carried its own date arithmetic.
 */
export type TaskScheduleTone = 'overdue' | 'due_today' | 'due_soon' | 'on_track' | 'none';

/** Calendar days from today to the end date; null when the task has none. */
export function taskDaysToDue(task: ProjectTask): number | null {
    if (!task.end_date) {
        return null;
    }

    return differenceInCalendarDays(new Date(task.end_date), new Date());
}

export function taskScheduleTone(task: ProjectTask): TaskScheduleTone {
    const days = taskDaysToDue(task);

    if (days === null || isTaskTerminalStatus(task.status)) {
        return 'none';
    }

    if (days < 0) {
        return 'overdue';
    }

    if (days === 0) {
        return 'due_today';
    }

    return days <= 7 ? 'due_soon' : 'on_track';
}
